import { rateTypeForBillingAccount } from '@/core/customer';

import type { ViewerRole } from '../components/EngagementHub/roles';
import { canApproveEngagementLive } from '../components/EngagementHub/roles';
import type { StatusTone } from '../components/EngagementHub/status';
import { formatEngagementMoney } from './money';
import type { EngagementView } from './viewModel';

export interface HubBanner {
  tone: StatusTone;
  title: string;
  body?: string;
}

/**
 * The status banner for the live Hub — what this engagement is waiting on, and
 * from whom.
 *
 * The frames are status-driven; the *contents* — the expert's decline reason and
 * the governance approver's adjustment or rejection — come from
 * `expertDeclineNote` and `governanceFeedback` (ENG-5604 G4). Both are withheld
 * from the expert by the api, so on the expert side they simply arrive null and
 * the frame renders without them; that is a view decision, not a missing value.
 */
export function hubBanner(
  view: EngagementView,
  viewerRole: ViewerRole,
  viewerUserId: string
): HubBanner | null {
  const isExpert = viewerRole === 'expert';

  switch (view.status) {
    case 'pending_offer':
      return isExpert
        ? { tone: 'attention', title: 'This request is waiting on your offer.' }
        : { tone: 'attention', title: 'Waiting for the expert to send an offer.' };

    case 'pending_confirmation':
      if (view.awaiting === 'governance') {
        // Roster, not the collapsed role: a player-coach holds work_approver
        // too, and `deriveViewerRole` returns that first — which handed the one
        // person who must decide the passive "waiting" frame, about themselves
        // (ENG-5641). Same read as the decision controls under this banner, so
        // the Hub and the inbox cannot disagree about who is deciding.
        return canApproveEngagementLive(view.status, view.awaiting, view.participants, viewerUserId)
          ? {
              tone: 'attention',
              title: 'Your approval is needed.',
              body: 'This engagement crossed the account governance threshold.',
            }
          : { tone: 'attention', title: 'Waiting for governance approval.' };
      }
      if (isExpert) return { tone: 'attention', title: 'Waiting for the customer to accept.' };
      return (
        governanceFeedbackBanner(view) ?? {
          tone: 'attention',
          title: 'Review and accept the offer.',
          body: governanceNote(view),
        }
      );

    case 'active':
      return fundingBanner(view, isExpert);

    case 'closing':
      return {
        tone: 'progress',
        title: 'This engagement is closing.',
        body: 'No new work is authorized. Hours for work performed before the closure date can still be submitted during the administrative window.',
      };

    case 'closed':
      return { tone: 'muted', title: 'This engagement is closed.', body: closureBody(view) };

    case 'canceled': {
      const declined = view.closureReason === 'expert_declined';
      // `KindWithdraw` is the only transition that stamps `canceled`
      // (`api/engagement/service/transition_verbs.go`), so the reason names the
      // exit unambiguously. Without this branch a withdrawn request fell to the
      // generic title and `closureBody` had no case for it either — a bare
      // frame with no body for both sides.
      const withdrawn = view.closureReason === 'canceled';
      return {
        tone: 'muted',
        title: declined
          ? 'The expert declined this request.'
          : withdrawn
            ? isExpert
              ? 'The customer withdrew this request.'
              : 'This request was withdrawn.'
            : 'This engagement was canceled.',
        // The expert's own words when they left any — a decline reason is the
        // whole point of asking for one. The expert side never receives it.
        body:
          (declined ? view.expertDeclineNote : null) ??
          (view.cancellationWindowEndsAt
            ? 'A reconciliation window is open for hours performed before cancellation.'
            : withdrawn
              ? 'It was withdrawn before the engagement started, so no hours were authorized.'
              : closureBody(view)),
      };
    }

    default:
      return null;
  }
}

/**
 * Governance bounced the offer back to the customer with an adjustment or a
 * rejection. This replaces the plain "review and accept" frame — the approver's
 * words and their proposed number are what the creator needs to act on, and
 * showing the ordinary accept prompt instead would hide that a control fired.
 *
 * The api returns this only while the engagement is awaiting the customer, so
 * there is no staleness check to make here.
 */
function governanceFeedbackBanner(view: EngagementView): HubBanner | null {
  const feedback = view.governanceFeedback;
  if (!feedback) return null;

  if (feedback.kind === 'rejection') {
    return {
      tone: 'attention',
      title: 'Governance rejected this engagement.',
      body: feedback.note ?? undefined,
    };
  }

  // `kind` is an open string on the wire ("adjustment" or "rejection" today).
  // A kind this code doesn't know must not be mislabeled an adjustment —
  // falling back to the ordinary accept frame is safe in every case, since the
  // api only returns feedback while the engagement is awaiting the customer.
  if (feedback.kind !== 'adjustment') return null;

  const proposed =
    feedback.proposedNteTotalCents !== null
      ? `Governance proposed a not-to-exceed total of ${formatEngagementMoney(
          feedback.proposedNteTotalCents,
          feedback.currency
        )}.`
      : null;
  return {
    tone: 'attention',
    title: 'Governance proposed an adjustment.',
    body: [proposed, feedback.note].filter(Boolean).join(' ') || undefined,
  };
}

/**
 * §6.3 role conflation: when the requester is also the governance approver the
 * approval step collapses, and the banner has to say so or the engagement looks
 * like it skipped a control.
 */
function governanceNote(view: EngagementView): string | undefined {
  if (!view.requiresGovernance) return undefined;
  return view.governanceRoleConflated === true
    ? 'This engagement is over the governance threshold. You hold the approver role, so accepting also records the approval.'
    : 'This engagement is over the governance threshold, so it goes to a governance approver after you accept.';
}

/**
 * Credit-funded engagements lock credits at activation; USD-funded ones invoice
 * in arrears. The classification comes from `core/customer`
 * (`BillingClassification.tla`) — never re-derived here — and the expert never
 * sees the customer's funding method at all (§6.3).
 */
function fundingBanner(view: EngagementView, isExpert: boolean): HubBanner | null {
  if (isExpert || !view.billing) return null;
  const rateType = rateTypeForBillingAccount({ isSMBAccount: view.billing.isSMBAccount });
  if (view.fundingMethod === 'credits' && rateType === 'credits') {
    return {
      tone: 'active',
      title: 'Credits are locked for this engagement.',
      body: 'Credits are drawn as hours are confirmed, up to the not-to-exceed total.',
    };
  }
  return null;
}

function closureBody(view: EngagementView): string | undefined {
  switch (view.closureReason) {
    case 'nte_consumed':
      return 'The not-to-exceed total was reached.';
    case 'end_date_reached':
      return 'The engagement reached its end date.';
    case 'manual_close':
      return 'Closed by a participant.';
    default:
      return undefined;
  }
}
